import type { ChatProvider } from '#types/plugin/chat'

interface SierraConfig {
  cssPath?: string
  hideLauncherOnHomepage?: string[]
  hideLauncher?: boolean
  launcher?: boolean
  launcherBackgroundColor?: string
}
interface SierraAPI {
  init: (config: any) => void
  cleanup: () => void
  openChatModal: () => void
  closeChatModal: () => void
  clearPersistedConversation: () => void
  endConversation: () => void
}

declare global {
  interface Window {
    sierra: SierraAPI
    sierraConfig: {
      waitForInit?: boolean
      customGreeting?: string
      onConversationStart?: () => void
      onClose?: () => void
      onOpen?: (opts: { isNewConversation: boolean }) => void
      variables?: {
        consumerId?: string
        isLoyaltyMember?: boolean
        locale: string
        brand: string
      }
    }
  }
}

export default (config?: SierraConfig): ChatProvider => {
  const { $feature, $gtm, $locale, $t } = useNuxtApp()
  const { spanishPages } = useAppConfig().pages.help
  const auth = useAuthStore()
  const user = useUserStore()
  const route = useRoute()
  const { brand, targetEnv } = useRuntimeConfig().public

  const { details, isLoyaltyMember } = storeToRefs(user)
  const { embedUrl: rawEmbedUrl } = $feature.configLivechat

  // Handle both string and object formats for embedUrl
  const embedUrl = computed(() => {
    if (!rawEmbedUrl) return null
    return typeof rawEmbedUrl === 'string' ? rawEmbedUrl : rawEmbedUrl[targetEnv.toUpperCase()] || null
  })

  const isAvailable = ref(false)
  const sierraReady = ref(false)
  const needsSierraUpdate = ref(false)

  const isSpanishPage = computed(() => route.path.split('/').some((page) => spanishPages?.includes(page)))
  const locale = isSpanishPage.value ? 'es-us' : $locale
  const translations = ref()

  window.sierraConfig = {
    waitForInit: true,
    variables: {
      brand,
      locale
    }
  }

  const isHomepage = computed(() => {
    const normalized = route.path.replace(/\/+$/, '')
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?)?$/.test(normalized)
  })

  const isCartOrCheckout = computed(() => route.name === 'cart' || route.name === 'checkout')

  const isCatalogOrSRP = computed(() => typeof route.name === 'string' && ['c-slug', 'search'].includes(route.name))

  const loadCss = async () => {
    const cssPath = config?.cssPath
    if (cssPath && !document.querySelector(`link[href="${cssPath}"]`)) {
      return new Promise((resolve, reject) => {
        try {
          useHead({
            link: () => [
              {
                rel: 'stylesheet',
                type: 'text/css',
                href: cssPath,
                once: true,
                onload: () => resolve(true),
                // sometimes it throws errors even when loading, so we just have to wait for the event to kick in so we don't block the loading flow, thats why its resolving and not rejecting
                onerror: () => resolve(false),
              }
            ],
          })
        }
        catch (err) {
          reject(err)
          console.warn('failed to load livechat css', err)
        }
      })
    }
  }

  const loadSpanishContent = async () => {
    translations.value = isSpanishPage.value ? await import('#server-assets-brand/i18n/texts/es-us.json') : $t
  }

  const updateSierraWithUserData = (profileDetails) => {
    if (!profileDetails) return
    const userData = {
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value,
        locale,
        brand
      }
    }
    Object.assign(window.sierraConfig, userData)

    const firstName = profileDetails.firstName || ''

    if (auth.loggedIn) {
      window.sierraConfig.customGreeting = isLoyaltyMember.value
        ? replaceAll(translations.value.sierraChatGreetingLoggedInAndLoyalty, {
            brandName: translations.value.brandName,
            firstName
          })
        : replaceAll(translations.value.sierraChatGreetingLoggedInAndNotLoyalty, {
            firstName
          })
    }
    else {
      window.sierraConfig.customGreeting = translations.value.sierraChatGreeting
    }
  }

  const setSierraConfig = () => {
    const sierraConfig = {
      canEndConversation: true,
      chatInterfaceStrings: {
        disclosure: translations.value.sierraChatDisclaimer
      },
      customGreeting: translations.value.sierraChatGreeting,
      dimensions: {
        width: '25rem',
        height: '40rem',
        maxHeight: 'calc(100% - 9rem)'
      },
      display: 'corner',
      titleBarLogoOnly: true
    }
    Object.assign(window.sierraConfig, sierraConfig)
  }

  const initializeLiveChatEventTracking = () => {
    window.sierraConfig.onOpen = () => $gtm?.push('livechat.onAction', 'Displayed', 'Sierra')
    window.sierraConfig.onConversationStart = () => {
      $gtm?.push('livechat.onAction', 'Started', 'Sierra')
      $gtm?.push('livechat.onAction', 'Agent Started', 'Sierra')
    }
    window.sierraConfig.onClose = () => $gtm?.push('livechat.onAction', 'Closed', 'Sierra')
  }

  const initSierra = () => {
    if (!window.sierra || !embedUrl.value) return

    window.sierra.cleanup?.()

    const config = {
      ...window.sierraConfig,
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value,
        locale,
        brand
      }
    }

    window.sierra.init(config)
    sierraReady.value = true
  }

  const setSierraChatVisibility = () => {
    const toggleBodyClass = (className, condition) => {
      document.body.classList.toggle(className, condition)
    }

    toggleBodyClass('hide-chat-button', isHomepage.value)
    toggleBodyClass('cart-page', isCartOrCheckout.value)
    toggleBodyClass('commerce-page', isCatalogOrSRP.value)
  }

  const setSierraChat = async () => {
    const isCookieAccepted = hasCookieConsent(['required', 'performanceAndAnalytics', 'functional', 'targeting'])
    try {
      if (!isCookieAccepted || !embedUrl.value) return

      setSierraChatVisibility()
      await loadCss()
      await loadSpanishContent()
      setSierraConfig()
      updateSierraWithUserData(details.value)
      initializeLiveChatEventTracking()

      loadScript(embedUrl.value!, { type: 'module' }).then(() => {
        initSierra()
        isAvailable.value = true
      })
    }
    catch (err) {
      console.error('Failed to load Sierra chat script:', err)
      isAvailable.value = false
    }
  }

  const openChat = () => {
    if (!sierraReady.value || needsSierraUpdate.value) {
    // Reinitialize with latest data before opening
      updateSierraWithUserData(details.value)
      initSierra()
      needsSierraUpdate.value = false
    }
    if (sierraReady.value && window.sierra) {
      window.sierra.openChatModal()
    }
    else {
      console.warn('Sierra chat not ready yet.')
    }
  }

  const closeChat = () => {
    if (window.sierra) {
      window.sierra.clearPersistedConversation()
      window.sierra.closeChatModal()
    }
  }

  watch(
    [
      () => auth.loggedIn,
      () => details.value,
      () => isHomepage.value,
      () => isCartOrCheckout.value,
      () => isCatalogOrSRP.value
    ],
    ([newAuthState, newDetails], [oldAuthState, oldDetails]) => {
      const authChanged = newAuthState !== oldAuthState
      const userChanged = newDetails !== oldDetails

      if (authChanged) {
        updateSierraWithUserData(newDetails)
        initSierra()
        // Clear and close on logout
        if (!newAuthState) {
          closeChat()
        }
      }
      else if (userChanged) {
        updateSierraWithUserData(newDetails)
        needsSierraUpdate.value = true
      }

      setSierraChatVisibility()
    },
    { immediate: true }
  )

  setSierraChat()

  return {
    provide: {
      chat: {
        open: openChat,
        available: isAvailable,
        launcher: !config?.hideLauncher
      }
    }
  }
}
